/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import {
  Modal,
  Form,
  Row,
  Col,
  Input,
  Button,
  Tag,
  DatePicker,
  Checkbox,
  Spin,
  Typography,
  notification,
} from 'antd';
import {
  SearchOutlined,
  FileExcelOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { getEmpleadosConDatosOpFilteredPaginated } from 'api/estructuraOrganizacional/empleados';
import { generarReporteEvaluacionesExcel } from 'api/configuraciones/clock/EvaluacionClock/EvaluacionGeneral';
import Table from 'components/Table';
import moment from 'moment';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const columnsResultados = [
  {
    titleText: 'No. Empleado',
    dataIndex: 'empleado',
    key: 'numero',
    width: 120,
    render: (emp) => emp?.numero_de_empleado,
  },
  {
    titleText: 'Nombre',
    dataIndex: 'empleado',
    key: 'nombre',
    width: 220,
    render: (emp) => emp?.nombre,
  },
  {
    titleText: 'Ap. Paterno',
    dataIndex: 'empleado',
    key: 'pat',
    width: 160,
    render: (emp) => emp?.apellido_paterno,
  },
  {
    titleText: 'Ap. Materno',
    dataIndex: 'empleado',
    key: 'mat',
    width: 160,
    render: (emp) => emp?.apellido_materno,
  },
];

const ReporteFlexibleModal = ({ open, onClose }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [resultadosBusqueda, setResultadosBusqueda] = useState([]);
  const [empleadosSeleccionados, setEmpleadosSeleccionados] = useState([]);
  const [sinFiltroFechas, setSinFiltroFechas] = useState(false);

  const handleBuscar = async () => {
    const numero = form.getFieldValue('buscar_numero')?.trim();
    const nombre = form.getFieldValue('buscar_nombre')?.trim();
    const apPat = form.getFieldValue('buscar_ap_pat')?.trim();

    if (!numero && !nombre && !apPat) {
      notification.warning({ message: 'Ingrese al menos un criterio de búsqueda.' });
      return;
    }

    setBuscando(true);
    try {
      let uri = 'catalogos/dato-operacional-del-empleado-paginado/?empleados_filter=';
      if (numero) uri += `numero_de_empleado=${numero};`;
      if (nombre) uri += `nombre=${nombre};`;
      if (apPat) uri += `apellido_paterno=${apPat};`;

      const res = await getEmpleadosConDatosOpFilteredPaginated(uri);
      setResultadosBusqueda(res?.results || []);
    } catch {
      notification.error({ message: 'Error al buscar empleados.' });
    } finally {
      setBuscando(false);
    }
  };

  const handleAgregar = (row) => {
    const numero = row?.empleado?.numero_de_empleado;
    if (!numero) return;
    const yaExiste = empleadosSeleccionados.some((e) => e.numero === numero);
    if (!yaExiste) {
      setEmpleadosSeleccionados((prev) => [
        ...prev,
        {
          numero,
          etiqueta: `${numero} - ${row?.empleado?.nombre || ''} ${row?.empleado?.apellido_paterno || ''}`.trim(),
        },
      ]);
    }
  };

  const handleQuitarEmpleado = (numero) => {
    setEmpleadosSeleccionados((prev) => prev.filter((e) => e.numero !== numero));
  };

  const handleGenerarReporte = async () => {
    if (!empleadosSeleccionados.length) {
      notification.warning({ message: 'Agregue al menos un empleado.' });
      return;
    }

    const numeros = empleadosSeleccionados.map((e) => e.numero);
    let fechaInicio = null;
    let fechaFin = null;

    if (!sinFiltroFechas) {
      const rango = form.getFieldValue('rango_fechas');
      if (!rango || !rango[0] || !rango[1]) {
        notification.warning({ message: 'Seleccione un rango de fechas o marque "Toda la historia".' });
        return;
      }
      fechaInicio = rango[0].format('YYYY-MM-DD');
      fechaFin = rango[1].format('YYYY-MM-DD');
    }

    setLoading(true);
    try {
      await generarReporteEvaluacionesExcel(numeros, fechaInicio, fechaFin);
      notification.success({ message: 'Reporte generado correctamente.' });
    } catch (err) {
      const msg = err?.response?.data?.error || 'Error al generar el reporte.';
      notification.error({ message: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleCerrar = () => {
    form.resetFields();
    setResultadosBusqueda([]);
    setEmpleadosSeleccionados([]);
    setSinFiltroFechas(false);
    onClose();
  };

  return (
    <Modal
      open={open}
      title={<Text style={{ fontSize: '1.1rem' }}>Reporte Flexible de Evaluaciones</Text>}
      onCancel={handleCerrar}
      footer={null}
      closable
      width="75%"
      destroyOnClose
    >
      <Spin spinning={loading} tip="Generando reporte...">
        <Form form={form} layout="vertical">

          {/* ── Bloque 1: Búsqueda ── */}
          <Text strong>1. Buscar y Agregar Empleados</Text>
          <Row gutter={8} style={{ marginTop: 8 }}>
            <Col xs={24} sm={5}>
              <Form.Item name="buscar_numero" label="No. de Empleado">
                <Input allowClear placeholder="Ej. 001" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item name="buscar_nombre" label="Nombre">
                <Input allowClear placeholder="Nombre(s)" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item name="buscar_ap_pat" label="Apellido Paterno">
                <Input allowClear placeholder="Apellido paterno" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={4} style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 24 }}>
              <Button
                icon={<SearchOutlined />}
                onClick={handleBuscar}
                loading={buscando}
                block
              >
                Buscar
              </Button>
            </Col>
          </Row>

          {resultadosBusqueda.length > 0 && (
            <Table
              cols={[
                ...columnsResultados,
                {
                  titleText: 'Acción',
                  dataIndex: 'id',
                  key: 'accion',
                  width: 100,
                  render: (_, row) => (
                    <Button size="small" type="link" onClick={() => handleAgregar(row)}>
                      + Agregar
                    </Button>
                  ),
                },
              ]}
              data={resultadosBusqueda}
              pagination={false}
              allowImport={false}
              allowExport={false}
              allowAdd={false}
              disableDelete
            />
          )}

          {/* ── Bloque 2: Empleados seleccionados ── */}
          {empleadosSeleccionados.length > 0 && (
            <div style={{ marginTop: 12, marginBottom: 8 }}>
              <Text strong>Empleados seleccionados:</Text>
              <div style={{ marginTop: 6 }}>
                {empleadosSeleccionados.map((e) => (
                  <Tag
                    key={e.numero}
                    closable
                    onClose={() => handleQuitarEmpleado(e.numero)}
                    icon={<DeleteOutlined />}
                    style={{ marginBottom: 4 }}
                  >
                    {e.etiqueta}
                  </Tag>
                ))}
              </div>
            </div>
          )}

          {/* ── Bloque 3: Fechas ── */}
          <div style={{ marginTop: 16 }}>
            <Text strong>2. Rango de fechas</Text>
            <Row gutter={12} style={{ marginTop: 8 }} align="middle">
              <Col xs={24} sm={14}>
                <Form.Item name="rango_fechas" label="Fecha inicio — Fecha fin">
                  <RangePicker
                    format="DD/MM/YYYY"
                    disabled={sinFiltroFechas}
                    disabledDate={(current) => current && current > moment().endOf('day')}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={10} style={{ paddingBottom: 24 }}>
                <Checkbox
                  checked={sinFiltroFechas}
                  onChange={(e) => {
                    setSinFiltroFechas(e.target.checked);
                    if (e.target.checked) form.setFieldValue('rango_fechas', null);
                  }}
                >
                  Toda la historia (sin filtro de fechas)
                </Checkbox>
              </Col>
            </Row>
          </div>

          {/* ── Acciones ── */}
          <Row justify="end" gutter={8} style={{ marginTop: 8 }}>
            <Col>
              <Button onClick={handleCerrar}>Cancelar</Button>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<FileExcelOutlined />}
                onClick={handleGenerarReporte}
                disabled={!empleadosSeleccionados.length}
              >
                Generar Excel
              </Button>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
};

export default ReporteFlexibleModal;
